/**
 * Judge scorecards: the 10-point must system, as pure functions.
 *
 * Used by the judge page (/judge/:token) and by the admin decision strip on
 * Manage Event's Matched tab. No Firebase, no React, no ESM-only packages, so
 * Jest can import it directly.
 *
 * The server re-validates every card in functions/judging.js. That file
 * mirrors CARD VALIDATION AND ROUND LOCKING; keep the two in step (judge-scoring.test.js
 * runs both against the same inputs).
 *
 * Card shape (judge-scorecards/{eventId}__{boutKey}__{judgeId}):
 *   rounds    [{ red, blue } | null] x 5   round n lives at rounds[n - 1]
 *   submitted boolean                       locked once true (an admin
 *                                           round unlock reopens it)
 *   lockedRounds [bool] x 5                 NEXT ROUND / submit lock a round
 *   endAt     1..5 | null                   last round fought; null = all 5
 *   method    "DEC" | "KO" | "TKO" | "SUB" | null   set on submit; SUB
 *                                           (submission) on MMA matches only
 *   winner    "red" | "blue" | null         present iff KO / TKO / SUB
 */

export const ROUNDS = 5;
export const SCORE_MIN = 6;
export const SCORE_MAX = 10;
export const METHODS = ["DEC", "KO", "TKO", "SUB"];
export const SIDES = ["red", "blue"];

const isInt = (n) => typeof n === "number" && Number.isInteger(n);

/** A single round: both sides 6..10, and one of them (or both) on 10. */
export function isValidRoundScore(score) {
    if (!score || typeof score !== "object") return false;
    const { red, blue } = score;
    if (!isInt(red) || !isInt(blue)) return false;
    if (red < SCORE_MIN || red > SCORE_MAX) return false;
    if (blue < SCORE_MIN || blue > SCORE_MAX) return false;
    return Math.max(red, blue) === SCORE_MAX;
}

export const emptyRounds = () => Array.from({ length: ROUNDS }, () => null);

/** The round shown when nothing has been entered yet. */
export const FRESH_ROUND = Object.freeze({ red: SCORE_MAX, blue: SCORE_MAX });

// KO, TKO and (MMA only) SUB end the fight: a winner, and the round it ended.
export const isStoppage = (method) => method === "KO" || method === "TKO" || method === "SUB";

/**
 * An MMA match: either corner's event-fighters `entry` is "MMA" (the
 * Partner API's `discipline`). Only MMA matches can end by submission.
 */
export const isMmaPair = (a, b) => a?.entry === "MMA" || b?.entry === "MMA";

/**
 * A card's scores with the corners the other way round: each round's red and
 * blue swapped, and the winner. The judge page keeps a card in the corner
 * order it was scored in; when an admin swaps the corners before any card
 * reached the server, the page turns its copy round with this and the server
 * turns a save scored the old way round (judgeSaveCard). Mirrored in
 * functions/judging.js.
 */
export function flipCorners(card) {
    const other = (side) => (side === "red" ? "blue" : side === "blue" ? "red" : side);
    return {
        ...card,
        rounds: Array.isArray(card.rounds) ? card.rounds.map((r) => (r ? { ...r, red: r.blue, blue: r.red } : r)) : card.rounds,
        ...("winner" in card ? { winner: other(card.winner) } : {}),
    };
}

/** The ways a judge can end a match: SUB (submission) only in MMA. */
export const methodsFor = (isMma) => (isMma ? METHODS : METHODS.filter((m) => m !== "SUB"));

/** How the admin pages spell a card method. */
export const METHOD_LABEL = Object.freeze({ DEC: "DECISION", KO: "KO", TKO: "TKO", SUB: "SUBMISSION" });

/**
 * Validate and normalise a save request. Pure; the server runs the same rules.
 *
 * Drafts may have gaps (a judge can jump between round tabs). A submit needs
 * every round 1..endAt (or 1..5) scored, a method, and a winner iff
 * KO/TKO/SUB. Rounds after endAt are dropped, never rejected. SUB is refused
 * (code SUB_NOT_MMA) when `isMma` is false; left out, the caller checks it.
 *
 * @returns {{ok: true, card: object} | {ok: false, error: string}}
 */
export function validateCardPayload({ rounds, submit, endAt, method, winner, commitRound, isMma } = {}) {
    if (!Array.isArray(rounds) || rounds.length !== ROUNDS) {
        return { ok: false, error: `Expected ${ROUNDS} rounds.` };
    }
    const clean = [];
    for (let i = 0; i < ROUNDS; i += 1) {
        const r = rounds[i];
        if (r === null || r === undefined) {
            clean.push(null);
        } else if (isValidRoundScore(r)) {
            clean.push({ red: r.red, blue: r.blue });
        } else {
            return { ok: false, error: `Round ${i + 1} is not a valid score.` };
        }
    }
    // NEXT ROUND commits (and locks) the round on screen, so it must be scored.
    if (commitRound !== null && commitRound !== undefined) {
        if (!isInt(commitRound) || commitRound < 1 || commitRound > ROUNDS) {
            return { ok: false, error: "The committed round must be between 1 and 5." };
        }
        if (!clean[commitRound - 1]) {
            return { ok: false, error: `Round ${commitRound} is not scored.` };
        }
    }

    if (!submit) {
        return {
            ok: true,
            card: { rounds: clean, submitted: false, endAt: null, method: null, winner: null },
        };
    }

    const last = endAt === null || endAt === undefined ? ROUNDS : endAt;
    if (!isInt(last) || last < 1 || last > ROUNDS) {
        return { ok: false, error: "The last round must be between 1 and 5." };
    }
    if (!METHODS.includes(method)) {
        return { ok: false, error: "The method must be DEC, KO, TKO or SUB." };
    }
    if (method === "SUB" && isMma === false) {
        return { ok: false, code: "SUB_NOT_MMA", error: "A submission finish is only for MMA matches." };
    }
    if (isStoppage(method)) {
        if (!SIDES.includes(winner)) {
            return { ok: false, error: `${method} needs a winner.` };
        }
    } else if (winner !== null && winner !== undefined) {
        return { ok: false, error: "A decision card has no winner field." };
    }
    for (let i = 0; i < last; i += 1) {
        if (!clean[i]) return { ok: false, error: `Round ${i + 1} is not scored.` };
    }
    for (let i = last; i < ROUNDS; i += 1) clean[i] = null;

    return {
        ok: true,
        card: {
            rounds: clean,
            submitted: true,
            endAt: last === ROUNDS ? null : last,
            method,
            winner: isStoppage(method) ? winner : null,
        },
    };
}

/* ── Round locking (mirrored in functions/judging.js) ──────────────────
 * NEXT ROUND locks the round on screen on the server; submit locks every
 * scored round. Drafts saved on a tab change or match switch lock nothing.
 * A locked round can't change until an admin unlocks it. */

const sameScore = (a, b) =>
    (!a && !b) || (!!a && !!b && a.red === b.red && a.blue === b.blue);

/**
 * Which rounds of a card are locked, as five booleans. Cards saved before
 * round locking have no lockedRounds: a submitted one counts as locked on
 * every scored round, a draft on none.
 */
export function lockedRoundsOf(card) {
    if (!card) return Array.from({ length: ROUNDS }, () => false);
    if (Array.isArray(card.lockedRounds)) {
        return Array.from({ length: ROUNDS }, (_, i) => card.lockedRounds[i] === true);
    }
    return Array.from(
        { length: ROUNDS },
        (_, i) => !!card.submitted && !!(Array.isArray(card.rounds) && card.rounds[i])
    );
}

/**
 * The lock check and new lock state for one save.
 * @param {object|null} stored  the card as stored, or null on a first save
 * @param {object} card  validateCardPayload's clean card
 * @param {{commitRound?: number|null}} [opts]
 */
export function applyRoundLocks(stored, card, { commitRound } = {}) {
    const locked = lockedRoundsOf(stored);
    const before = stored && Array.isArray(stored.rounds) ? stored.rounds : [];
    for (let i = 0; i < ROUNDS; i += 1) {
        if (locked[i] && !sameScore(before[i], card.rounds[i])) {
            return {
                ok: false,
                code: "ROUND_LOCKED",
                round: i + 1,
                error: `Round ${i + 1} is locked. Ask an admin to unlock it.`,
            };
        }
    }
    // Rounds lock in order: NEXT ROUND may lock only the first round that
    // isn't locked yet (every earlier one locked), or a round an admin
    // unlocked for correction. Stops a double tap or a look at a later tab
    // from locking a round nobody scored.
    if (commitRound && !card.submitted && !locked[commitRound - 1]) {
        const inOrder = locked.slice(0, commitRound - 1).every(Boolean);
        const reopened = !!(stored && stored.pendingRevisions && stored.pendingRevisions[String(commitRound)]);
        if (!inOrder && !reopened) {
            const open = locked.indexOf(false) + 1;
            return {
                ok: false,
                code: "ROUND_ORDER",
                round: commitRound,
                error: `Lock round ${open} before round ${commitRound}.`,
            };
        }
    }
    const next = locked.slice();
    const newlyLocked = [];
    const lockOne = (i) => {
        if (!next[i] && card.rounds[i]) {
            next[i] = true;
            newlyLocked.push(i + 1);
        }
    };
    if (card.submitted) {
        for (let i = 0; i < ROUNDS; i += 1) lockOne(i);
    } else if (commitRound) {
        lockOne(commitRound - 1);
    }
    return { ok: true, lockedRounds: next, newlyLocked };
}

/**
 * One −/+ press. Returns the new score, or null when the press isn't allowed:
 * past 6..10, or it would leave neither side on 10. (To hand a 10–9 round to
 * the other corner: raise the 9 to 10 first, then lower the old 10.)
 */
export function stepScore(score, side, delta) {
    const cur = score ?? FRESH_ROUND;
    if (!SIDES.includes(side)) return null;
    const next = { red: cur.red, blue: cur.blue };
    next[side] += delta;
    return isValidRoundScore(next) ? next : null;
}

/** Round numbers (1-based) up to `last` with no score, ignoring `except`. */
export function missingRounds(rounds, last = ROUNDS, except = null) {
    const out = [];
    for (let n = 1; n <= last; n += 1) {
        if (n !== except && !rounds?.[n - 1]) out.push(n);
    }
    return out;
}

export const scoredCount = (rounds) => (rounds ?? []).filter(Boolean).length;

export function tallyRounds(rounds) {
    return (rounds ?? []).reduce(
        (t, r) => (r ? { red: t.red + r.red, blue: t.blue + r.blue } : t),
        { red: 0, blue: 0 }
    );
}

/** Who this card has winning: the stoppage winner, or the side ahead on points. */
export function cardPick(card) {
    if (!card) return null;
    if (isStoppage(card.method)) return card.winner ?? null;
    const t = tallyRounds(card.rounds);
    if (t.red > t.blue) return "red";
    if (t.blue > t.red) return "blue";
    return "draw";
}

/**
 * Which fighter is in which corner for one bout. Red is always shown on the
 * LEFT, blue on the RIGHT.
 *
 * `events/{id}.boutCorners[pairKey]` holds the red fighter's id once an admin
 * has swapped corners. With no entry, or an entry that names neither fighter
 * (a stale id after a re-match), the default applies: fighterA (the Matched
 * tab's left fighter) is red.
 *
 * @param {object} fighterA  event-fighter ({ fighterId, ... })
 * @param {object} fighterB
 * @param {string} [redFighterId]  boutCorners[pairKey]
 * @returns {{red: object, blue: object, swapped: boolean}}
 */
export function resolveCorners(fighterA, fighterB, redFighterId) {
    if (redFighterId && fighterB && redFighterId === fighterB.fighterId) {
        return { red: fighterB, blue: fighterA, swapped: true };
    }
    return { red: fighterA, blue: fighterB, swapped: false };
}

/** judge-scorecards doc id. `boutKey` is manage-event's pairKey. */
export const scorecardId = (eventId, boutKey, judgeId) =>
    `${eventId}__${boutKey}__${judgeId}`;

/**
 * One judge's cell in the admin decision strip.
 * @returns {{state: "in"|"scoring"|"out"|"revoked", total, pick, scored, card}}
 */
export function judgeCell(judge, card) {
    const total = tallyRounds(card?.rounds);
    const scored = scoredCount(card?.rounds);
    if (judge?.revokedAt) {
        return { state: "revoked", total, pick: null, scored, card };
    }
    if (card?.submitted) {
        return { state: "in", total, pick: cardPick(card), scored, card };
    }
    return { state: scored > 0 ? "scoring" : "out", total, pick: null, scored, card };
}

/**
 * The suggested result for one bout from its judges' cards.
 *
 * - A stoppage is a fact, not a tally: as soon as one submitted card says KO,
 *   TKO or SUB, it is suggested, provided every submitted card agrees on the
 *   method, the winner and the round it ended in. Any disagreement
 *   (including a DEC card beside a KO card, or KO R2 beside KO R3) is a
 *   conflict (reason "disagree") and nothing is suggested. So is a SUB card
 *   on a match that isn't MMA (reason "sub_not_mma"): a corner's entry
 *   changed after it went in, and only the admin can say how it ended.
 * - Otherwise it waits until every active (non-revoked) judge has submitted,
 *   then counts picks: a side picked by more than half the judges wins
 *   (Unanimous / Split / Majority); anything else is a Draw.
 *
 * @param {Array<object>} judges every judge on the event ({id, revokedAt})
 * @param {Object<string, object>} cardsByJudge this bout's cards, by judgeId
 * @param {{isMma?: boolean}} [opts] isMma: false when neither corner entered
 *   MMA (isMmaPair); left out, SUB isn't checked against the discipline
 */
export function boutVerdict(judges, cardsByJudge, { isMma } = {}) {
    const active = (judges ?? []).filter((j) => !j.revokedAt);
    const total = active.length;
    if (total === 0) return { status: "none", in: 0, total };

    const inCards = active
        .map((j) => cardsByJudge?.[j.id])
        .filter((c) => c?.submitted);
    const done = inCards.length;

    const stoppages = inCards.filter((c) => isStoppage(c.method));
    if (stoppages.length > 0) {
        const first = stoppages[0];
        // A submission on a match that isn't MMA (a corner's entry changed
        // after the card went in) isn't a finish anyone can confirm: the
        // admin decides.
        if (isMma === false && inCards.some((c) => c.method === "SUB")) {
            return { status: "conflict", reason: "sub_not_mma", in: done, total };
        }
        // Every card must agree on the method, the winner and the round it
        // ended in (no end round = the last).
        const endOf = (c) => c.endAt ?? ROUNDS;
        const agree = inCards.every(
            (c) => c.method === first.method && c.winner === first.winner && endOf(c) === endOf(first)
        );
        if (!agree) {
            return { status: "conflict", reason: "disagree", in: done, total };
        }
        return {
            status: "stoppage",
            kind: first.method,
            method: first.method,
            side: first.winner,
            rounds: [endOf(first)],
            in: done,
            total,
        };
    }

    if (done < total) return { status: "waiting", in: done, total };

    const picks = inCards.map(cardPick);
    const red = picks.filter((p) => p === "red").length;
    const blue = picks.filter((p) => p === "blue").length;
    const draw = picks.filter((p) => p === "draw").length;
    const line = (w, l) => (draw > 0 ? `${w}–${l}–${draw}` : `${w}–${l}`);

    for (const [side, w, l] of [
        ["red", red, blue],
        ["blue", blue, red],
    ]) {
        if (w * 2 > total) {
            const kind = w === total ? "Unanimous" : l > 0 ? "Split" : "Majority";
            return { status: "decision", kind, side, line: line(w, l), in: done, total };
        }
    }
    return {
        status: "decision",
        kind: "Draw",
        side: "draw",
        line: `${red}–${blue}–${draw}`,
        in: done,
        total,
    };
}

/** How manage-event records a win, by card method. */
export const WIN_BY = Object.freeze({ DEC: "decision", KO: "ko", TKO: "tko", SUB: "submission" });

/**
 * The result the judges' cards point to for one bout, in manage-event's own
 * terms, or null when there is none yet (cards still out, judges disagree
 * on a stoppage, no judges).
 *
 * @param {Array<object>} judges
 * @param {Object<string, object>} cardsByJudge this bout's cards, by judgeId
 * @param {string} aId  one fighter's id (any order)
 * @param {string} bId  the other fighter's id
 * @param {object} [matchedFighter] the bout's current selections
 * @param {{isMma?: boolean}} [opts] passed to boutVerdict
 * @returns {null | {isDraw, winnerId, loserId, winBy, applied, verdict}}
 *   applied: the current selections already equal the suggestion.
 */
export function judgeSuggestion(judges, cardsByJudge, aId, bId, matchedFighter, { isMma } = {}) {
    const verdict = boutVerdict(judges, cardsByJudge, { isMma });
    if (verdict.status !== "stoppage" && verdict.status !== "decision") return null;
    // Cards record which fighter stood in which corner; without one, the
    // default order (a = red) applies.
    const anyCard = Object.values(cardsByJudge ?? {})[0];
    const cornerId = (side) =>
        side === "red" ? anyCard?.redFighterId ?? aId : anyCard?.blueFighterId ?? bId;
    const isDraw = verdict.side === "draw";
    const winnerId = isDraw ? null : cornerId(verdict.side);
    const loserId = isDraw ? null : winnerId === aId ? bId : aId;
    const winBy = isDraw ? null : WIN_BY[verdict.method ?? "DEC"];
    const applied = isDraw
        ? !!matchedFighter?.isDraw
        : matchedFighter?.winId === winnerId &&
          !matchedFighter?.isDraw &&
          matchedFighter?.winBy === winBy;
    return { isDraw, winnerId, loserId, winBy, applied, verdict };
}

/** True when the admin has already picked a result for the bout by hand. */
export const hasResult = (matchedFighter) =>
    !!matchedFighter?.isDraw || !!matchedFighter?.winId;
